import { getTagFromNode } from '@msdk/common'
import {
  ControlValidationType,
  DurationFieldDefinition,
  DurationValue,
  FormFieldDefinition,
  FormOpenMode,
} from '@msdk/definitions'
import { FormField, FormFieldLabel, TextInput, Typography } from '@msdk/ui'
import React, { FocusEvent } from 'react'
import { NumberFormatValues, PatternFormat, SourceInfo } from 'react-number-format'
import { useParams } from 'react-router-dom'

import { useFieldLabel, useFieldSeverity, useFieldValue, useHasMandatoryValidation } from '../../../hooks'
import { FieldProps } from '../../../models/form'
import { nodeHasFlag } from '../../../utils'
import { ValidationMessages } from '../../ValidationMessages'

type DurationUnit = 'hh' | 'mm' | 'ss'

const VALID_FORMATS = ['hh:mm:ss', 'hh:mm', 'mm:ss', 'hh', 'mm', 'ss']

const DEFAULT_UNITS: DurationUnit[] = ['hh', 'mm', 'ss']

const UNIT_TO_DURATION_KEY: Record<DurationUnit, 'hours' | 'minutes' | 'seconds'> = {
  hh: 'hours',
  mm: 'minutes',
  ss: 'seconds',
}

export function DurationField({ node, onChange, fieldArgs }: FieldProps) {
  const fieldDef = node.value.fieldDefinition as DurationFieldDefinition
  const formDef = node.value.formDefinition as FormFieldDefinition
  const fieldLabel = useFieldLabel(fieldDef, formDef)

  const [value, setValue] = useFieldValue(node, onChange)
  const { moduleName } = useParams()

  const hasMandatoryFlag = nodeHasFlag(node, ControlValidationType.Mandatory)
  const hasMandatory = useHasMandatoryValidation(node, fieldDef.entityName)

  const isValidFormat = !!fieldDef.format && VALID_FORMATS.includes(fieldDef.format.toLowerCase())

  // The units derived from the format, in order (e.g. ['hh', 'mm', 'ss']). Falls back to hh:mm:ss when the configured format is missing or invalid.
  const units = isValidFormat ? (fieldDef.format.toLowerCase().split(':') as DurationUnit[]) : DEFAULT_UNITS

  /**
   * Get the format shape string to use for the pattern input based on the units to be displayed in the duration field.
   * @param units The units to be displayed in the duration field, in order (e.g. ['mm', 'ss']).
   * @returns The format shape string (e.g. "##:##") to use for the pattern input.
   */
  const getFormatShape = (unitsList: string[]) => unitsList.map(() => '##').join(':')

  const checkNumber = (n: number) => {
    if (Number.isNaN(n)) {
      return 0
    }
    return n
  }

  const processValue = (stringValue: string) => {
    const parts = stringValue.split(':')

    const durationObj = { hours: 0, minutes: 0, seconds: 0 }
    let hasValue = false

    units.forEach((unit, index) => {
      const parsed = parseInt(parts[index], 10)
      // Make sure minutes and seconds are within 0-59 range; hours are unbounded.
      const bounded = unit === 'hh' ? parsed : Math.min(59, parsed)

      if (!Number.isNaN(bounded)) {
        hasValue = true
      }
      durationObj[UNIT_TO_DURATION_KEY[unit]] = checkNumber(bounded)
    })

    node.value.isDirty = true
    if (hasValue) {
      const dur = new DurationValue(durationObj)
      setValue(dur)
    } else {
      setValue(null)
    }
  }

  /**
   * Handle the change on the `onBlur` event when the field is not complete,
   * to fill the missing digits if the user leaves the field before finishing.
   */
  const handleBlur = (e: FocusEvent<HTMLInputElement>) => {
    const newValue = e.target.value
    processValue(newValue)
  }

  /**
   * Handle the change when the user clears the field or finishes entering all the digits,
   * so validation runs without waiting for the field to lose focus.
   * SourceInfo is required to ignore changes that do not come from the user, such as the form load.
   */
  const handleValueChange = (values: NumberFormatValues, sourceInfo: SourceInfo) => {
    if (!sourceInfo.event) {
      return
    }
    if (!values.value) {
      node.value.isDirty = true
      setValue(null)
    } else if (values.value.length === units.length * 2) {
      processValue(values.formattedValue)
    }
  }

  const isReadOnly = node.value.state === 'Readonly'
  const severity = useFieldSeverity(fieldDef, node)

  return (
    <FormField
      required={(hasMandatory || hasMandatoryFlag) && fieldArgs?.formOpenMode !== FormOpenMode.Readonly}
      size="small"
      data-test-id={getTagFromNode(fieldDef, moduleName)}
      severity={severity}
      readOnly={isReadOnly}
      disabled={isReadOnly}
    >
      <FormFieldLabel>{fieldLabel}</FormFieldLabel>
      <PatternFormat
        format={getFormatShape(units)}
        mask="_"
        onBlur={handleBlur}
        onValueChange={handleValueChange}
        label={fieldArgs?.showLabel ? fieldLabel : undefined}
        disabled={isReadOnly}
        placeholder={units.join(':').toUpperCase()}
        value={value ? (value as DurationValue).value?.toFormat(units.join(':')) ?? '' : ''}
        inputProps={{
          disabled: isReadOnly,
        }}
        endAdornment={<Typography variant="caption">{units.join(':')}</Typography>}
        customInput={TextInput}
      />
      <ValidationMessages messages={node.value.validationMessages} fieldDef={fieldDef} node={node} />
    </FormField>
  )
}
